/* eslint-disable no-console */

/**
 * NAV TRACE — the browser-console flight recorder for internal navigation
 * (Sandy 2026-08-31).
 * ---------------------------------------------------------------------------
 * Every click that should land on a case / staff / contact (header search,
 * inbox cards, missed-call cards) logs its WHOLE journey here: the route the
 * click built, what the destination page read from the URL, every fallback
 * branch it took, and where the selection finally landed — or exactly which
 * rule refused it.
 *
 * One navigation = one numbered trace (`[nav#7 +123ms] …`). Every line is a
 * single self-contained string (details inlined as JSON), so a straight
 * copy-paste of the console is a complete, readable report.
 *
 * Usage:
 *   navTraceStart('search: case click', { caid, cid })  // a click begins
 *   navTrace('reconcile: company', { via: 'url' })      // a step on the way
 *   navTraceOk('case selected', { caid })               // landed (green)
 *   navTraceFail('rule 3: not in active set', { want }) // refused (red)
 *
 * A step logged with NO trace running (a hard page load on a deep URL, a
 * back/forward) auto-opens one named "(direct arrival)" — those journeys
 * matter just as much.
 *
 * Always on, on purpose: it's plain console.log, invisible unless the
 * console is open, and the whole point is that Sandy can see it live in
 * prod the moment a link misbehaves.
 */

let seq = 0
let current = null

const STYLE_START = 'color:#8e24aa;font-weight:bold'
const STYLE_STEP = 'color:#1565c0'
const STYLE_OK = 'color:#2e7d32;font-weight:bold'
const STYLE_FAIL = 'color:#c62828;font-weight:bold'

/** Small, safe, single-line JSON for the details — never throws. */
function inline(detail) {
  if (detail === undefined) return ''
  try {
    return ` — ${JSON.stringify(detail)}`
  }
  catch {
    return ' — [unserializable]'
  }
}

function line(style, text) {
  console.log(`%c${text}`, style)
}

export function navTraceStart(source, detail) {
  seq += 1
  current = { id: seq, t0: performance.now() }
  line(STYLE_START, `[nav#${seq}] ▶ ${source}${inline(detail)}`)
}

function prefix() {
  if (!current) navTraceStart('(direct arrival)')
  const ms = Math.round(performance.now() - current.t0)

  return `[nav#${current.id} +${ms}ms]`
}

export function navTrace(step, detail) {
  line(STYLE_STEP, `${prefix()} ${step}${inline(detail)}`)
}

/** The journey landed where it should — green, and the trace stays open for stragglers. */
export function navTraceOk(outcome, detail) {
  line(STYLE_OK, `${prefix()} ✔ ${outcome}${inline(detail)}`)
}

/** The journey was refused / fell back — red. */
export function navTraceFail(outcome, detail) {
  line(STYLE_FAIL, `${prefix()} ✘ ${outcome}${inline(detail)}`)
}
